import React, { useEffect, useState } from "react";
import {
  Box,
  Modal,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { ModalDrawerStyles, ModalHeader } from "../../Common/styles/modal";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../Common/styles/table";
import { CloudDownload } from "@mui/icons-material";
import { toastMessage, errorToastMessage } from "../../../utils/toast";
import StatusBadge from "../../Common/UI/StatusTag";
import http from "../../../utils/http";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

export const documentStatusColor: any = {
  active: {
    variant: "active",
    label: "Active",
  },
  inactive: {
    variant: "cancelled",
    label: "Inactive",
  },
};

type VersionHistoryItem = {
  id: string;
  version: string;
  uploadDate: string;
  status: "active" | "inactive";
  fileUrl: string;
  documentId: string;
};

type Props = {
  showModal: boolean;
  closeModal: () => void;
  documentName: string;
  documentType: string;
  documentCategory: string;
  documentId: string;
};

const VersionHistoryModal = ({
  showModal,
  closeModal,
  documentName,
  documentType,
  documentCategory,
  documentId,
}: Props) => {
  const [downloadLoading, setDownloadLoading] = useState("");
  const [versions, setVersions] = useState<VersionHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { participantId } = useParams();
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        setLoading(true);
        const res = await http.get(
          `/participant-documents/registry/${documentId}?participantId=${participantId}`
        );
        const formattedData = res.data?.data?.versions.map((version: any) => ({
          id: version.id,
          version: version.version ?? 1,
          uploadDate: DateTime.fromISO(version.createdAt).toFormat(
            "dd/MM/yyyy hh:mm a"
          ),
          status: version.isActive ? "active" : "inactive",
          fileUrl: version.url,
          documentId: version.participantDocumentId,
        }));
        formattedData?.sort((a: any, b: any) => b.version - a.version);
        setVersions(formattedData);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchVersions();
  }, [documentId, participantId]);

  const handleDownload = async (id: string, versionId: string) => {
    try {
      setDownloadLoading(id);
      let res = await http.get(
        `/participant-documents/registry/${id}/version/${versionId}?participantId=${participantId}`
      );

      const downloadLink = res?.data?.data?.previewUrl;
      window.open(downloadLink, "_blank");
      toastMessage("success", res.data.message);
      setDownloadLoading("");
    } catch (err) {
      setDownloadLoading("");
      errorToastMessage(err as Error);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title="Version History" onCloseClick={closeModal} />
        <Box sx={{ pt: 2 }}>
          <Box sx={{ mb: 3, display: "flex", gap: 4 }}>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={"regular"}
                sx={{ mb: 1 }}
              >
                Document Name
              </Typography>
              <Typography variant="subtitle1">{documentName || "-"}</Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={"regular"}
                sx={{ mb: 1 }}
              >
                Document Type
              </Typography>
              <Typography variant="subtitle1">{documentType || "-"}</Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={"regular"}
                sx={{ mb: 1 }}
              >
                Document Category
              </Typography>
              <Typography variant="subtitle1" textTransform="capitalize">
                {documentCategory || "-"}
              </Typography>
            </Box>
          </Box>

          <Table sx={TableBorderRadiusTopLeftRight}>
            <TableHead>
              <TableRow>
                <StyledTableCell>Version</StyledTableCell>
                <StyledTableCell>Upload Date</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Download</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                versions.length > 0 &&
                versions.map((version: VersionHistoryItem) => (
                  <TableRow key={version.id}>
                    <StyledTableCell>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "regular",
                          color: "text.primary",
                        }}
                      >
                        {version.version}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "regular",
                          color: "text.primary",
                        }}
                      >
                        {version.uploadDate}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <StatusBadge
                        label={
                          documentStatusColor[version?.status]?.label ??
                          version.status
                        }
                        variant={documentStatusColor[version?.status]?.variant}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box>
                        {downloadLoading === version.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <>
                            <IconButton
                              onClick={() =>
                                handleDownload(version.documentId, version.id)
                              }
                              disabled={!!downloadLoading}
                            >
                              <CloudDownload />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {!loading && versions.length === 0 && (
            <NoDataContainer>
              <Typography variant="body1" color="gray">
                No versions found
              </Typography>
            </NoDataContainer>
          )}
          {loading && (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default VersionHistoryModal;
